import {
  balanceSheet,
  trialBalanceReport,
  type BalanceLine,
  type TrialBalanceRow,
} from '@khata/core';
import { PageHead } from '../components/index.js';
import { taka } from '../lib/format.js';
import { t } from '../lib/i18n.js';
import { master, state } from '../lib/khata.js';
import type { StringKey } from '../lib/strings.js';

function lineLabel(l: BalanceLine): string {
  switch (l.kind) {
    case 'account':
      return l.name;
    case 'stock':
      return t('line_stock');
    case 'defective':
      return t('line_defective');
    case 'capital':
      return t('line_capital');
    case 'profit':
      return t('line_profit');
    case 'drawings':
      return t('line_drawings');
    default:
      return t(`line_${l.kind}` as StringKey, { name: l.name });
  }
}

function trialLabel(r: TrialBalanceRow): string {
  if (r.kind === 'account' || r.kind === 'party') return r.name;
  return t(`tb_${r.kind}` as StringKey);
}

function Group(props: {
  title: string;
  lines: ReadonlyArray<BalanceLine>;
  totalLabel: string;
  total: number;
}) {
  return (
    <div style={{ marginBottom: 24 }}>
      <h6 class="section">{props.title}</h6>
      {props.lines.map((l, i) => (
        <div key={l.kind + l.id + i} class="kv">
          <span>{lineLabel(l)}</span>
          <span class="mono">{taka(l.amount as never)}</span>
        </div>
      ))}
      <div class="kv">
        <strong>{props.totalLabel}</strong>
        <strong class="mono">{taka(props.total as never)}</strong>
      </div>
    </div>
  );
}

/** Balance sheet as of today and the trial balance behind it. */
export function Books() {
  const s = state.value;
  const m = master.value;
  const bs = balanceSheet(s, m);
  const tb = trialBalanceReport(s, m);
  const ok = bs.balanced && tb.balanced;

  return (
    <>
      <PageHead title={t('books_title')} sub={t('books_sub')} />
      <div class={'banner' + (ok ? ' info' : '')} data-testid="books-status">
        {ok ? t('books_balanced') : t('books_notBalanced')}
      </div>
      <h6 class="section" style={{ marginTop: 24 }}>
        {t('books_balanceSheet')}
      </h6>
      <section class="two-col" data-testid="balance-sheet">
        <div>
          <Group
            title={t('books_assets')}
            lines={bs.assets}
            totalLabel={t('books_totalAssets')}
            total={bs.totalAssets}
          />
        </div>
        <div>
          <Group
            title={t('books_liabilities')}
            lines={bs.liabilities}
            totalLabel={t('books_totalLiabilities')}
            total={bs.totalLiabilities}
          />
          <Group
            title={t('books_equity')}
            lines={bs.equity}
            totalLabel={t('books_totalEquity')}
            total={bs.totalEquity}
          />
          <div class="kv">
            <strong>{t('books_liabilitiesPlusEquity')}</strong>
            <strong class="mono" data-testid="liabilities-plus-equity">
              {taka((bs.totalLiabilities + bs.totalEquity) as never)}
            </strong>
          </div>
        </div>
      </section>
      <h6 class="section" style={{ marginTop: 40 }}>
        {t('books_trialBalance')}
      </h6>
      <div style={{ overflowX: 'auto' }}>
        <table class="table" data-testid="trial-balance">
          <thead>
            <tr>
              <th>{t('books_ledger')}</th>
              <th style={{ textAlign: 'right' }}>{t('books_debit')}</th>
              <th style={{ textAlign: 'right' }}>{t('books_credit')}</th>
            </tr>
          </thead>
          <tbody>
            {tb.rows.map((r) => (
              <tr key={r.kind + r.id}>
                <td>{trialLabel(r)}</td>
                <td class="mono" style={{ textAlign: 'right' }}>
                  {r.debit ? taka(r.debit) : ''}
                </td>
                <td class="mono" style={{ textAlign: 'right' }}>
                  {r.credit ? taka(r.credit) : ''}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td>
                <strong>{t('books_total')}</strong>
              </td>
              <td class="mono" style={{ textAlign: 'right' }}>
                <strong data-testid="tb-debit">{taka(tb.totalDebit)}</strong>
              </td>
              <td class="mono" style={{ textAlign: 'right' }}>
                <strong data-testid="tb-credit">{taka(tb.totalCredit)}</strong>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </>
  );
}
